import React, { Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { logErrorToService } from '../utils/errorHandler';

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree and displays a fallback UI
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console
    console.error('Error caught by ErrorBoundary:', error, errorInfo);

    // Update state with error details
    this.setState((prevState) => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));

    // Log error to error tracking service
    logErrorToService(error, {
      componentStack: errorInfo.componentStack,
      errorCount: this.state.errorCount + 1,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Call custom reset handler if provided
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleGoToLogin = () => {
    // Clear local storage
    localStorage.clear();

    // Redirect to login page
    window.location.href = '/login';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI from props
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleReset);
      }

      // Default fallback UI
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          errorCount={this.state.errorCount}
          onReset={this.handleReset}
          onGoToLogin={this.handleGoToLogin}
          showDetails={process.env.NODE_ENV === 'development'}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Default Error Fallback UI
 */
const ErrorFallback = ({ error, errorInfo, errorCount, onReset, onGoToLogin, showDetails }) => {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        {/* Error Icon */}
        <div className="text-center">
          <div className="mx-auto h-24 w-24 text-red-500 flex items-center justify-center bg-red-100 rounded-full">
            <ExclamationTriangleIcon className="h-16 w-16" />
          </div>
        </div>

        {/* Error Message */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Oops! Something went wrong
          </h1>
          <p className="text-lg text-gray-600 mb-4">
            We encountered an unexpected error. Please try again or return to the login page.
          </p>

          {errorCount > 1 && (
            <p className="text-sm text-orange-600 mb-4">
              This error has occurred {errorCount} times. If the problem persists, please contact support.
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={onReset}
            className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            <ArrowPathIcon className="h-5 w-5 mr-2" />
            Try Again
          </button>

          <button
            onClick={onGoToLogin}
            className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            Go to Login
          </button>
        </div>

        {/* Error Details (Development Only) */}
        {showDetails && error && (
          <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">Error Details (Development Mode)</h3>
            </div>
            <div className="px-6 py-4">
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Error Message:</h4>
                  <p className="text-sm text-red-600 font-mono bg-red-50 p-3 rounded border border-red-200">
                    {error.toString()}
                  </p>
                </div>

                {errorInfo && errorInfo.componentStack && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">Component Stack:</h4>
                    <pre className="text-xs text-gray-600 bg-gray-50 p-3 rounded border border-gray-200 overflow-auto max-h-64">
                      {errorInfo.componentStack}
                    </pre>
                  </div>
                )}

                {error.stack && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">Stack Trace:</h4>
                    <pre className="text-xs text-gray-600 bg-gray-50 p-3 rounded border border-gray-200 overflow-auto max-h-64">
                      {error.stack}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * HOC to wrap component with error boundary
 * @param {React.Component} Component - Component to wrap
 * @param {Object} errorBoundaryProps - Props to pass to ErrorBoundary
 * @returns {React.Component}
 */
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  return (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );
};

/**
 * Hook to use error boundary imperatively
 * Note: This is a simplified version. For production, consider using react-error-boundary library
 */
export const useErrorHandler = () => {
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return setError;
};

export default ErrorBoundary;